import { css, cx } from '@linaria/core'
import { type CSSProperties, type FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '#app/store'
import type { TruckConfiguration } from '#app/store/slices/configurationSlice'
import { ConfigurationStatusIcon } from '#pages/Configuration/components/ConfigurationStatusIcon'
import { DenominationAndPartPeriod } from '#pages/Configuration/components/DenominationAndPartPeriod'
import { extractConfigurationTitle } from '#pages/Configuration/components/extractConfigurationTitle'
import { fragments, style } from '#styles/index'
import { formatDuration } from '#utils/formatDuration'
import type { ChangeTableContext } from '../useChangesTable'

const headerClass = css`
  align-self: stretch;
  position: sticky;
  top: 0;
  z-index: 1;
  height: 65px;
  background: ${style.theme.backgroundSurfacePrimary};
  display: grid;
  grid-column: 1/14;
  align-items: center;

  > ._base-title,
  > ._comparison-title > ._title {
    ${fragments.textStyle.heading.s.regular}
  }

  > ._base-title {
    padding: 16px 20px 8px 16px;
    display: flex;
    grid-column: 6/13;
    align-items: flex-start;
    justify-items: flex-start;
    height: 100%;
    color: ${style.theme.textAccent};
    background: ${style.theme.backgroundSurfacePrimary};
    gap: 16px;
  }

  > ._comparison-title {
    padding: 16px 24px 8px 24px;
    display: flex;
    align-items: flex-start;
    justify-items: flex-start;
    grid-column: 1/6;
    gap: 16px;
    height: 100%;
    color: ${style.theme.textSoft};
    background-color: ${style.theme.backgroundSurfaceTertiary};
  }

  > * > ._changed {
    height: 100%;
    display: flex;
    align-items: flex-end;
    padding-bottom: 19px;
    ${fragments.textStyle.heading.xs.subtle}
  }
`

const comparisonHeaderClass = css`
  display: grid;
  grid-template-columns: auto max-content;
  grid-template-rows: min-content min-content;
  grid-column-gap: 4px;

  &._empty {
    height: 44px;
    min-width: 250px;
    justify-self: stretch;
  }

  > * {
    display: flex;
    align-self: center;
    align-items: center;
  }

  > ._index {
    justify-self: center;
  }

  > ._name {
    gap: 4px;
    ${fragments.textStyle.heading.s.regular};
  }

  > ._denomination {
    display: flex;
    gap: 10px;
  }

  > ._index, > ._denomination {
    ${fragments.textStyle.body.xs.regular}
  }


  > ._index, > ._denomination, > ._status {
    opacity: 0.5;
  }
`

const ComparisonHeaderTitle: FC<{ configuration?: TruckConfiguration; index?: number; isComparedItem?: boolean }> = ({
  configuration,
  index,
  isComparedItem,
}) => {
  const { t } = useTranslation()
  if (!configuration || index === undefined) {
    return <div className={cx(comparisonHeaderClass, '_empty')}> </div>
  }

  const configurationTitle = extractConfigurationTitle(configuration, t)

  return (
    <div className={comparisonHeaderClass}>
      {isComparedItem ? <div /> : <ConfigurationStatusIcon configuration={configuration} isActive={!isComparedItem} />}
      <div className="_name">{configurationTitle}</div>
      {isComparedItem ? <div /> : <div className="_index">{index + 1}</div>}
      <DenominationAndPartPeriod configuration={configuration} />
    </div>
  )
}

export const ChangeHeaderComponent: FC<{ context: ChangeTableContext; style?: CSSProperties }> = ({ context, style }) => {
  const configurations = useAppSelector((x) => x.truckConfigurations.configurations)
  const index = useMemo(
    () => Object.values(configurations).findIndex((x) => x?.id === context.baseConfiguration?.configuration.id),
    [configurations, context.baseConfiguration?.configuration.id],
  )

  const base = context.baseConfiguration
  const comparison = context.comparisonConfiguration

  return (
    <div className={cx('_row', headerClass)} style={style}>
      <div className="_comparison-title">
        <ComparisonHeaderTitle configuration={comparison?.configuration} index={index} isComparedItem />
        <span className="_changed">{formatDuration(Date.now() - (context.timestamps.snapshotTime ?? 0))}</span>
      </div>
      <div className="_base-title">
        <ComparisonHeaderTitle configuration={base?.configuration} index={index} />
        <span className="_changed">{formatDuration(Date.now() - (context.timestamps.lastChange ?? 0))}</span>
      </div>
    </div>
  )
}
